// skillgenpro-frontend/src/error-framework/ErrorContext.jsx
//
// The React side of the framework: holds the single "what error is active
// right now" state, decides (via recoveryEngine) whether to retry silently
// or surface it, and exposes `useErrorHandler()` for any component to
// report a failure through.
//
// Mount `<ErrorProvider>` once, inside `<BrowserRouter>` (it needs
// `useLocation` for context resolution) and outside/above anything that
// might need to report an error — see the App.js wiring in the design doc.
// `<ErrorExperience />` (a sibling component) reads this same context to
// render the UI; ErrorContext itself renders no UI.

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { normalizeError } from './errorMapper';
import { resolveContext } from './contextResolver';
import { getRecoveryPlan, withAutoRetry } from './recoveryEngine';

const ErrorContext = createContext(null);

export function ErrorProvider({ children }) {
  const location = useLocation();
  const [activeError, setActiveError] = useState(null);
  const [pendingRetryFn, setPendingRetryFn] = useState(null);
  const [retryState, setRetryState] = useState(null); // { attempt, delayMs, error } while auto-retrying

  /**
   * Report a failure. If the classified error is auto-retry-eligible AND a
   * `retryFn` was supplied, this attempts recovery silently (with backoff)
   * before ever showing anything — that's the Phase 10 self-healing path.
   * Otherwise (or once auto-retry is exhausted) it surfaces the error via
   * ErrorExperience, keeping `retryFn` around so the user's own "Retry"
   * click can re-attempt the exact same operation.
   *
   * @param {*} rawError
   * @param {Object} [overrides] - e.g. { feature: 'checkout', aiSubject: 'your message' }
   * @param {() => Promise<any>} [retryFn] - re-runs the failed operation
   */
  const reportError = useCallback(
    async (rawError, overrides, retryFn) => {
      const ctx = resolveContext(location.pathname, overrides);
      const normalized = normalizeError(rawError, ctx);
      const plan = getRecoveryPlan(normalized);

      if (plan.shouldAutoRetry && typeof retryFn === 'function') {
        try {
          const result = await withAutoRetry(
            retryFn,
            (e) => normalizeError(e, ctx),
            (attempt, delayMs, err) => setRetryState({ attempt, delayMs, error: err })
          );
          setRetryState(null);
          return { recovered: true, result };
        } catch (finalError) {
          setRetryState(null);
          setActiveError(finalError);
          setPendingRetryFn(() => retryFn);
          return { recovered: false, error: finalError };
        }
      }

      setActiveError(normalized);
      setPendingRetryFn(() => (typeof retryFn === 'function' ? retryFn : null));
      return { recovered: false, error: normalized };
    },
    [location.pathname]
  );

  const dismissError = useCallback(() => {
    setActiveError(null);
    setPendingRetryFn(null);
    setRetryState(null);
  }, []);

  /** Invoked by ErrorExperience's "Retry" button — makes exactly one manual attempt. */
  const retryActiveError = useCallback(async () => {
    const fn = pendingRetryFn;
    const prevContext = activeError ? activeError.context : {};
    setActiveError(null);
    setPendingRetryFn(null);
    if (typeof fn !== 'function') return;
    try {
      await fn();
    } catch (err) {
      await reportError(err, prevContext, fn);
    }
  }, [pendingRetryFn, activeError, reportError]);

  const value = useMemo(
    () => ({ activeError, retryState, reportError, dismissError, retryActiveError }),
    [activeError, retryState, reportError, dismissError, retryActiveError]
  );

  return <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>;
}

/**
 * Primary hook for components: `const { reportError } = useErrorHandler();`
 * Safe to call even if a provider isn't mounted above it (e.g. in an
 * isolated test) — falls back to console logging rather than crashing.
 */
export function useErrorHandler() {
  const ctx = useContext(ErrorContext);
  if (!ctx) {
    return {
      activeError: null,
      retryState: null,
      reportError: async (err) => {
        // eslint-disable-next-line no-console
        console.error('[useErrorHandler] called outside <ErrorProvider> — no UI will show:', err);
        return { recovered: false, error: err };
      },
      dismissError: () => {},
      retryActiveError: async () => {},
    };
  }
  return ctx;
}
